import { API_BASE_URL, REFRESH_KEY } from './ApiService';
import { ownAccessToken } from './accessToken';
import { peekDeviceSigningKey } from './deviceSigningKeySlot';
import { unregisterMachineKeyAtSignOut } from './machineKeyRegistration';
import { captureHeldRefreshSecrets } from './refreshSecretWriteAhead';

/**
 * I-0927, AC-8: sign-out ends the session on the server, not only in this browser. Every request
 * carries `keepalive` so it outlives the page, and none is awaited — signing out never waits for the
 * network, and a failure leaves this browser signed out all the same.
 *
 * I-1634 (ADR-100's I-1634 amendment): **the first request leaves in this tick**, before anything is
 * awaited. The refresh secrets are wrapped (I-1625), so naming one takes an async unwrap, and a page
 * closed or navigated away in that gap sent nothing — the session stayed alive on the server until
 * it expired. The access token is in hand synchronously and names the credential (`cid`), so
 * `POST /auth/logout/current` goes first, and the browser hands a started `keepalive` request to its
 * network process whatever the page does next.
 *
 * The secret-naming logouts still follow, read in this call and unwrapped after, so the clear that
 * follows cannot empty the slots first. They cover an access token that has already run out, which
 * `logout/current` cannot verify. The server answers both 204 and revokes once, whichever lands
 * first.
 *
 * ⛔ *Mutation:* send `logout/current` after the unwrap (the order before I-1634) → `signOut.test.ts`
 * "starts a revoke in the same tick" and `sign-out-survives-unload.spec.ts` are red.
 */
export function endServerSession(): void {
  // D-0347, ADR-175 (AC-MK.5): this machine's key goes with the person, and leaves first, because the
  // logout below revokes the session that proves whose machine it is.
  unregisterMachineKeyAtSignOut();
  // F-0489 (§3.1): the device signing key goes with the person too, and its revocation leaves first.
  // A page whose key module never loaded holds no key (`deviceSigningKeySlot.ts`).
  peekDeviceSigningKey()?.forgetDeviceSigningKeyAtSignOut();
  void logoutByAccessToken()?.catch(() => undefined);
  void heldRefreshSecrets().then((secrets) => {
    for (const refreshToken of secrets) {
      void logout(refreshToken).catch(() => undefined);
    }
  });
}

/**
 * Sign-out named by the access token, started synchronously (I-1634). `fetch` with `keepalive`
 * rather than `navigator.sendBeacon`, because a beacon cannot carry `Authorization`, and the route is
 * bearer only so CSRF does not apply. `undefined` when there is no access token.
 */
function logoutByAccessToken(): Promise<Response> | undefined {
  const accessToken = ownAccessToken();
  if (!accessToken) return undefined;
  try {
    return fetch(`${API_BASE_URL}/auth/logout/current`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      keepalive: true,
    });
  } catch {
    // A fetch that throws in the call must not stop the sign-out it accompanies.
    return undefined;
  }
}

/**
 * Every refresh secret this browser holds for the session: the current one and any successor chosen
 * for it whose rotation was never confirmed (I-1623). After a lost answer the server holds one of
 * the successors and has rotated past the current one, so a sign-out naming the current one alone
 * would find nothing and leave the session alive. Naming each is safe: sign-out revokes whichever
 * the server knows and answers the rest the same, so it is no oracle (I-0927).
 *
 * ⛔ *Mutation:* name the current secret only → `signOut.test.ts`'s lost-answer case goes red.
 */
function heldRefreshSecrets(): Promise<string[]> {
  return captureHeldRefreshSecrets(REFRESH_KEY).catch(() => []);
}

/**
 * Async so a `fetch` that throws in the call becomes this promise's rejection: thrown in the loop
 * above, it would stop the loop, leave every later secret unnamed and their sessions alive, and
 * reach the page as an unhandled rejection (gate 742).
 */
async function logout(refreshToken: string): Promise<Response> {
  return fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${refreshToken}` },
    keepalive: true,
  });
}

/** I-1242: how long the sheet's hand-over waits for its own session to end before it goes on. */
export const SHEET_SESSION_END_WAIT_MS = 2_000;

/**
 * I-1242 (F-0534, the captain's ruling): the app's sign-in sheet signed in only to hand the app its
 * code, so once the code is issued that session ends on the server, and the page waits for it: the
 * redirect that follows closes the sheet, and a request still in flight then may never be sent.
 * Bounded, so a slow answer never holds the hand-over; the app's own session, issued when it redeems
 * the code, is a separate one and is not touched.
 */
export async function endSheetSession(timeoutMs = SHEET_SESSION_END_WAIT_MS): Promise<void> {
  // The bound starts now, so reading the wrapped secrets (I-1625) counts against it too.
  const bound = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
  // I-1634: the access token's revoke leaves at once, as at sign-out.
  const byAccessToken = logoutByAccessToken()?.then(
    () => undefined,
    () => undefined,
  );
  const bySecrets = heldRefreshSecrets().then((secrets) =>
    Promise.all(
      secrets.map((refreshToken) =>
        logout(refreshToken).then(
          () => undefined,
          () => undefined,
        ),
      ),
    ),
  );
  const request = Promise.all([byAccessToken, bySecrets]);
  await Promise.race([request.then(() => undefined), bound]);
}
